/**
 * UserAvatar — the one avatar every surface (marketing header, sidebar, chat
 * ownership) renders for a person.
 *
 * Email-signup accounts never carry an `avatar_url`: only OAuth hands us a
 * picture. Falling back to a grey initial made those users look half-broken and
 * made two people with the same first letter indistinguishable. Instead we
 * generate a deterministic marble from the identity seed (email, lowercased) so
 * the same person renders the same gradient on soku.ai and inside the product,
 * on every device, with no network request and no stored asset.
 *
 * A real `avatarUrl` always wins; the marble is what shows when there is none
 * (or when the image 404s, since Radix falls through to the fallback).
 */
'use client'

import BoringAvatar from 'boring-avatars'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

/**
 * Soku orange, dark ember → cream. Marble blends adjacent stops, so the range
 * has to stay inside one hue family or the result reads as a random gradient
 * rather than as brand.
 */
export const AVATAR_COLORS = [
  '#7c2d12',
  '#c2410c',
  '#e8590c',
  '#fb923c',
  '#fcd9a8',
]

export function UserAvatar({
  seed,
  name,
  avatarUrl,
  size = 'default',
  className,
}: {
  /** Stable identity key — use the email so it matches across surfaces. */
  seed: string
  /** Display name, used for the image alt text and the SVG title. */
  name: string
  avatarUrl?: string | null
  size?: 'default' | 'sm' | 'lg'
  className?: string
}) {
  return (
    <Avatar size={size} className={cn('rounded-full', className)}>
      {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
      <AvatarFallback className="rounded-full bg-transparent">
        <BoringAvatar
          variant="marble"
          name={seed.trim().toLowerCase()}
          colors={AVATAR_COLORS}
          size="100%"
          title
          aria-label={name}
        />
      </AvatarFallback>
    </Avatar>
  )
}
